import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Statistic,
  Steps,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { calcForward, type ForwardOutput } from '@noumase/cylinder-calc';
import { useMotorLibrary } from './motorStore';
import { StepCard, type StepState } from './StepCard';

const { Text } = Typography;

// 常用减速机比档位（1 = 无减速机）；与反算候选集合 ReversePanel.DEFAULT_REDUCERS 同步
const REDUCER_RATIOS = [1, 3, 4, 5, 7, 10, 12, 15, 20, 25, 30, 35, 40, 45, 50];
const REDUCER_OPTIONS = REDUCER_RATIOS.map((v) => ({ value: v, label: String(v) }));

// 常用同步轮减速比档位（1 = 无同步轮）
const SYNC_DEC_RATIOS = [1, 1.2, 1.4, 1.6, 2];
const SYNC_DEC_OPTIONS = SYNC_DEC_RATIOS.map((v) => ({ value: v, label: String(v) }));

// 丝杆导程按功率分档：< 400W → [4,5]；400~4000W → [5,10]；≥ 4000W → [20]
const TWO_PI = 2 * Math.PI;
function motorMechPowerW(torque: number | undefined, speed: number | undefined): number {
  if (!torque || !speed || torque <= 0 || speed <= 0) return 0;
  return (torque * TWO_PI * speed) / 60;
}
function leadCandidatesByPower(powerW: number): number[] {
  if (powerW < 400) return [4, 5];
  if (powerW < 4000) return [5, 10];
  return [20];
}

interface FormValues {
  motorTorque: number;
  motorSpeed: number;
  lead: number;
  reducerRatio: number;
  syncDecRatio: number;
  efficiency: number;
  gravity: number;
}

const DEFAULT_FORM: FormValues = {
  motorTorque: 5,
  motorSpeed: 3000,
  lead: 5,
  reducerRatio: 1,
  syncDecRatio: 1.2,
  efficiency: 0.85,
  gravity: 9.8,
};

function formatPower(w: number): string {
  return w >= 1000 ? `${(w / 1000).toString().replace(/\.?0+$/, '')}KW` : `${w}W`;
}

function formatPowerPrecise(w: number): string {
  return w >= 1000 ? `${(w / 1000).toFixed(2)} KW` : `${w.toFixed(2)} W`;
}

// 对应桌面程序「电机扭矩 / 电机转速」label 的黄色高亮
const highlightLabelStyle: CSSProperties = {
  background: '#fff7d6',
  padding: '2px 6px',
  borderRadius: 4,
};

export default function ForwardPanel() {
  const { t } = useTranslation();
  const { motors } = useMotorLibrary();
  const [form] = Form.useForm<FormValues>();
  const [output, setOutput] = useState<ForwardOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickedMotorId, setPickedMotorId] = useState<string | null>(null);

  // 监听扭矩/转速：动态算功率 → 决定 lead 候选；同时作为步骤 1 完成判定
  const watchedTorque = Form.useWatch('motorTorque', form);
  const watchedSpeed = Form.useWatch('motorSpeed', form);
  const watchedLead = Form.useWatch('lead', form);
  const motorPowerW = useMemo(
    () => motorMechPowerW(watchedTorque, watchedSpeed),
    [watchedTorque, watchedSpeed],
  );
  const leadCandidates = useMemo(() => leadCandidatesByPower(motorPowerW), [motorPowerW]);
  const leadOptions = useMemo(
    () => leadCandidates.map((v) => ({ value: v, label: String(v) })),
    [leadCandidates],
  );
  useEffect(() => {
    if (watchedLead !== undefined && !leadCandidates.includes(watchedLead)) {
      form.setFieldValue('lead', leadCandidates[0]);
    }
  }, [leadCandidates, watchedLead, form]);

  const motorReady =
    !!watchedTorque && watchedTorque > 0 && !!watchedSpeed && watchedSpeed > 0;
  const hasResult = output !== null;

  const motorStepState: StepState = hasResult ? 'completed' : 'active';
  const driveStepState: StepState = !motorReady
    ? 'inactive'
    : hasResult
      ? 'completed'
      : 'active';
  const resultStepState: StepState = !hasResult ? 'inactive' : 'active';

  const topCurrent = hasResult ? 2 : motorReady ? 1 : 0;

  const runCalc = () => {
    const values = form.getFieldsValue(true);
    setError(null);
    try {
      setOutput(calcForward(values));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setOutput(null);
    }
  };

  const handleReset = () => {
    form.setFieldsValue(DEFAULT_FORM);
    setOutput(null);
    setError(null);
    setPickedMotorId(null);
  };

  const handleMotorPick = (id: string) => {
    const m = motors.find((x) => x.id === id);
    if (!m) return;
    form.setFieldsValue({
      motorTorque: m.ratedTorque,
      motorSpeed: m.ratedSpeed,
    });
    setPickedMotorId(id);
  };

  const brands = useMemo(
    () => Array.from(new Set(motors.map((m) => m.brand))).sort((a, b) => a.localeCompare(b)),
    [motors],
  );
  const [selectedBrand, setSelectedBrand] = useState<string | null>(
    brands.length === 1 ? (brands[0] ?? null) : null,
  );
  useEffect(() => {
    if (selectedBrand && !brands.includes(selectedBrand)) {
      setSelectedBrand(brands.length === 1 ? (brands[0] ?? null) : null);
    } else if (!selectedBrand && brands.length === 1) {
      setSelectedBrand(brands[0] ?? null);
    }
  }, [brands, selectedBrand]);

  const motorsOfBrand = useMemo(
    () =>
      selectedBrand
        ? [...motors]
            .filter((m) => m.brand === selectedBrand)
            .sort((a, b) => a.power - b.power)
        : [],
    [motors, selectedBrand],
  );

  const clearAll = (
    <Button type="link" size="small" onClick={handleReset} style={{ padding: 0 }}>
      ⊗ {t('reverse.steps.clearAll')}
    </Button>
  );

  return (
    <>
      <Steps
        current={topCurrent}
        size="small"
        items={[
          { title: t('forward.steps.motor') },
          { title: t('forward.steps.drive') },
          { title: t('forward.steps.result') },
        ]}
        style={{
          marginBottom: 16,
          padding: '12px 24px',
          background: '#f7f8fa',
          border: '1px solid #ebedf0',
          borderRadius: 4,
        }}
      />

      <Form
        form={form}
        layout="horizontal"
        initialValues={DEFAULT_FORM}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        labelAlign="left"
        colon={false}
        preserve
      >
        {/* 步 1：电机参数 — 包含品牌/功率快速选取 + 扭矩/转速 */}
        <StepCard
          index={1}
          title={t('forward.steps.motor')}
          state={motorStepState}
          extra={clearAll}
        >
          <Row gutter={16}>
            <Col xs={24} md={14}>
              <Card
                type="inner"
                size="small"
                title={t('forward.quickPickTitle')}
                style={{ background: '#fff' }}
                styles={{ body: { padding: 12, background: '#fff' } }}
              >
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>
                    {t('forward.brandPickTitle')}
                  </div>
                  <Space size={8} wrap>
                    {brands.length === 0 ? (
                      <span style={{ color: '#bbb' }}>{t('forward.noBrand')}</span>
                    ) : (
                      brands.map((b) => (
                        <Button
                          key={b}
                          type={selectedBrand === b ? 'primary' : 'default'}
                          onClick={() => {
                            setSelectedBrand(b);
                            setPickedMotorId(null);
                          }}
                        >
                          {b}
                        </Button>
                      ))
                    )}
                  </Space>
                </div>
                {selectedBrand ? (
                  <>
                    <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>
                      {t('forward.powerPickTitle')}
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                        gap: 8,
                      }}
                    >
                      {motorsOfBrand.map((m) => (
                        <Button
                          key={m.id}
                          type={pickedMotorId === m.id ? 'primary' : 'default'}
                          onClick={() => handleMotorPick(m.id)}
                          title={`${m.brand}${m.series ? ' ' + m.series : ''}${m.model ? ' · ' + m.model : ''} · T=${m.ratedTorque.toFixed(2)} N·m / R=${m.ratedSpeed} r/min`}
                          style={{ height: 44, fontSize: 15, fontWeight: 600 }}
                        >
                          {formatPower(m.power)}
                        </Button>
                      ))}
                    </div>
                  </>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('forward.brandPickPrompt')}
                  />
                )}
              </Card>
            </Col>
            <Col xs={24} md={10}>
              <Card
                type="inner"
                size="small"
                title={t('forward.manualInputTitle')}
                style={{ background: '#fff' }}
                styles={{ body: { padding: 12, background: '#fff' } }}
              >
              <Form.Item
                label={<span style={highlightLabelStyle}>{t('forward.fields.torque')}</span>}
                name="motorTorque"
                rules={[
                  { required: true, message: t('common.required') },
                  { type: 'number', min: 0.0001, message: t('common.gtZero') },
                ]}
              >
                <InputNumber style={{ width: '100%' }} step={0.1} />
              </Form.Item>
              <Form.Item
                label={<span style={highlightLabelStyle}>{t('forward.fields.speed')}</span>}
                name="motorSpeed"
                rules={[
                  { required: true, message: t('common.required') },
                  { type: 'number', min: 1, message: t('common.gtZero') },
                ]}
              >
                <InputNumber style={{ width: '100%' }} step={100} />
              </Form.Item>
              </Card>
            </Col>
          </Row>
        </StepCard>

        {/* 步 2：传动配置 + 其他系数 */}
        <StepCard
          index={2}
          title={t('forward.steps.drive')}
          state={driveStepState}
          placeholder={t('forward.steps.drivePlaceholder')}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t('forward.fields.lead')}
                name="lead"
                extra={t('forward.fields.leadExtra')}
                rules={[
                  { required: true, message: t('common.required') },
                  { type: 'number', min: 0.01, message: t('common.gtZero') },
                ]}
              >
                <Select style={{ width: '100%' }} options={leadOptions} />
              </Form.Item>
              <Form.Item
                label={t('forward.fields.reducer')}
                name="reducerRatio"
                extra={t('forward.fields.reducerExtra')}
                rules={[{ type: 'number', min: 0.0001, message: t('common.gtZero') }]}
              >
                <Select style={{ width: '100%' }} options={REDUCER_OPTIONS} />
              </Form.Item>
              <Form.Item
                label={t('forward.fields.syncDec')}
                name="syncDecRatio"
                extra={t('forward.fields.syncDecExtra')}
                rules={[{ type: 'number', min: 0.0001, message: t('common.gtZero') }]}
              >
                <Select style={{ width: '100%' }} options={SYNC_DEC_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={t('forward.fields.efficiency')}
                name="efficiency"
                tooltip={t('forward.fields.efficiencyTip')}
                rules={[{ type: 'number', min: 0.01, max: 1, message: t('common.rangeZeroOne') }]}
              >
                <InputNumber style={{ width: '100%' }} step={0.05} />
              </Form.Item>
              <Form.Item
                label={t('forward.fields.gravity')}
                name="gravity"
                tooltip={t('forward.fields.gravityTip')}
                rules={[{ type: 'number', min: 0.1, message: t('common.gtZero') }]}
              >
                <InputNumber style={{ width: '100%' }} step={0.1} />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleReset}>{t('common.reset')}</Button>
              <Button type="primary" onClick={runCalc}>
                {hasResult ? t('forward.steps.recalc') : t('forward.submit')}
              </Button>
            </Space>
          </div>
        </StepCard>

        {/* 步 3：计算结果 */}
        <StepCard
          index={3}
          title={t('forward.steps.result')}
          state={resultStepState}
          placeholder={t('forward.steps.resultPlaceholder')}
        >
          {error && (
            <Alert
              type="error"
              message={t('common.calcError')}
              description={error}
              style={{ marginBottom: 12 }}
              closable
              onClose={() => setError(null)}
            />
          )}
          {output === null && !error ? (
            <Empty description={t('forward.empty')} />
          ) : output ? (
            <>
              <Row gutter={32}>
                <Col xs={24} sm={12}>
                  <Statistic
                    title={t('forward.result.thrust')}
                    value={output.thrust}
                    precision={2}
                    suffix="KG"
                    valueStyle={{ color: '#1677ff', fontSize: 40, fontWeight: 600 }}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Statistic
                    title={t('forward.result.speed')}
                    value={output.speed}
                    precision={2}
                    suffix="mm/s"
                    valueStyle={{ color: '#52c41a', fontSize: 40, fontWeight: 600 }}
                  />
                </Col>
              </Row>
              <Row
                gutter={16}
                style={{ marginTop: 16, paddingTop: 12, borderTop: '1px dashed #e6e6e6' }}
              >
                <Col xs={24} sm={12}>
                  <Statistic
                    title={t('forward.result.totalRatio')}
                    value={output.totalRatio}
                    precision={3}
                    valueStyle={{ fontSize: 18 }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('forward.result.ratioFormula')}
                  </Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Statistic
                    title={t('forward.result.motorPower')}
                    value={formatPowerPrecise(output.motorPower)}
                    valueStyle={{ fontSize: 18 }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('forward.result.powerFormula')}
                  </Text>
                </Col>
              </Row>
            </>
          ) : null}
        </StepCard>
      </Form>
    </>
  );
}
